// The homepage, as one flight: dusk above the clouds with GG ☸ BHARAT among them (Hero), down through the cloud floor
// onto India in cut paper (PaperIndia), into Chandigarh (PaperCity), postcards from inside the game (Inside), the
// stage of games, shows and events (Stage), then your name, the prices, questions and the way in. The sky is WebGL,
// the maps are paper sheets in CSS 3D, everything else is CSS; under "reduce motion" it all holds still.
import { useEffect, useRef, useState } from 'react';
import { PASS, REG } from '../brand';
import { SiteFooter, SiteNav } from '../Chrome';
import { PassField } from '../Pass';
import { useNameCheck } from '../Plate';
import { PlayCard } from '../PlayCard';
import { openRegister, useRegConfig } from '../Register';
import { linkProps } from '../router';
import { Hero } from './Hero';
import { Inside } from './Inside';
import { PaperCity } from './PaperCity';
import { PaperIndia } from './PaperIndia';
import { Scenes, ScenesCtx } from './scenes';
import { Stage } from './Stage';
import '../home.css';
import './landing.css';

const FAQ: [string, string][] = [
  ['What is GG Bharat?', 'An independent studio from Chandigarh building India’s worlds in 3D: open-world games of real Indian cities, and live shows and events inside them. Everything runs in the browser, on a phone or a laptop.'],
  ['What is Chandigarh: The City Beautiful?', 'Our first game: Chandigarh rebuilt at 1:1 from open map data and hundreds of photographs. Walk, ride, drive or fly through the real city, with other players, right in your browser.'],
  [`What do I get for ₹${REG.fee}?`, 'Your GG Bharat name, reserved for good: nobody else can ever take it, in any of our games, shows or events. A founder number, given in the order people register. And a place among the first players in when Chandigarh opens.'],
  ['Why do names cost anything?', `So the good ones go to real people, not to bots and hoarders: one name a person, ₹${REG.fee}, taxes included.`],
  ['Do I need an account too?', 'It’s free, and worth it: make it with the same email as your name and the name is on your account. Log in any time to see your place on the waitlist; on launch day you just log in.'],
  ['When can I play?', 'Not yet: Chandigarh is in a closed beta and isn’t open to the public. Join the waitlist and founders get in first; we’ll email you the day it opens.'],
  ['How much is the game itself?', `At launch, the ${PASS.name} costs ₹${PASS.price} once, for lifetime access to Chandigarh. No subscription.`],
  ['Do I need a powerful phone?', 'No download, no install. It runs in Chrome, Edge, Safari or Firefox; a phone from the last few years gives the best ride.'],
  ['Can I get my money back?', `Yes: ask within ${REG.refundDays} days of paying and we refund the ₹${REG.fee} in full. See the refund policy.`],
  ['Is this run by the government or the traffic police?', 'No. GG Bharat is an independent studio. It isn’t run by, or linked to, the Chandigarh Administration, the traffic police or any government body.'],
  ['What happens to my details?', 'Your email and phone are only for your registration, your receipt and telling you when the game opens. Never shown to other players, never sold. See the privacy policy.'],
];

/** your name, as a pass, checked as you type */
function Claim({ id }: { id: string }) {
  const [name, setName] = useState('');
  const check = useNameCheck(name);
  const cfg = useRegConfig();
  return (
    <div className="cl-claim">
      <PassField value={name} onChange={setName} check={check} id={id} />
      <button type="button" className="gx-btn lg" onClick={() => openRegister(name)} disabled={check.status === 'bad'}>
        Join the waitlist · ₹{REG.fee} <span aria-hidden>→</span>
      </button>
      <p className="gx-meta">
        {cfg && cfg.count >= REG.showCountFrom ? <b>{cfg.count.toLocaleString('en-IN')} on the waitlist · </b> : null}
        Joining reserves this name for you. Founder numbers go in order; one name a person, taxes included.
      </p>
    </div>
  );
}

/** scroll too fast past the city and its famous cameras catch you (once a visit, a laptop only, never under reduce) */
function SpeedCam({ scroller }: { scroller: React.RefObject<HTMLDivElement | null> }) {
  const [ticket, setTicket] = useState<number | null>(null);
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    const el = scroller.current;
    if (!el || !matchMedia('(pointer: fine)').matches || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let last = el.scrollTop;
    let lastT = performance.now();
    let v = 0;
    let caught = sessionStorage.getItem('gg.caught') === '1';
    let wheel = 0;
    let raf = 0;
    const onWheel = () => (wheel = performance.now());
    el.addEventListener('wheel', onWheel, { passive: true });
    const tick = () => {
      const t = performance.now();
      const dy = t - wheel < 220 ? Math.abs(el.scrollTop - last) : 0;
      const dt = Math.max(16, t - lastT);
      last = el.scrollTop;
      lastT = t;
      v = v * 0.86 + (dy / dt) * 1000 * 0.02 * 0.14;
      // only past the sky: the flight down is meant to be quick
      if (!caught && v > 78 && el.scrollTop > el.clientHeight * 9) {
        caught = true;
        sessionStorage.setItem('gg.caught', '1');
        setFlash(true);
        setTimeout(() => setFlash(false), 240);
        setTicket(Math.round(v));
        setTimeout(() => setTicket(null), 6500);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('wheel', onWheel);
    };
  }, [scroller]);
  return (
    <>
      {flash && <div className="kz-flash" aria-hidden />}
      <div className={`kz-caught${ticket ? ' on' : ''}`} role="status" aria-live="polite">
        <b className="mono">e-Challan · ggbharat.com</b>
        <span>Over-scrolling: {ticket ?? 0} in a 60 zone. Fine: ₹0. Slow down, the city is worth a look.</span>
      </div>
    </>
  );
}

let boarded = false;

export function Landing() {
  const root = useRef<HTMLDivElement>(null);
  const [scenes, setScenes] = useState<Scenes | null>(null);
  const [faq, setFaq] = useState<number | null>(0);
  const [board, setBoard] = useState(false);

  useEffect(() => {
    const el = root.current!;
    const s = new Scenes(el);
    setScenes(s);
    const bar = el.querySelector<HTMLElement>('.gx-progress');
    const onScroll = () => {
      if (bar) bar.style.transform = `scaleX(${el.scrollTop / Math.max(1, el.scrollHeight - el.clientHeight)})`;
      // the boarding pass, once you've seen Chandigarh (it would cover the map before)
      const city = el.querySelector<HTMLElement>('#chandigarh');
      if (!boarded && city && el.scrollTop > city.offsetTop + city.offsetHeight - el.clientHeight * 0.5) {
        boarded = true;
        setTimeout(() => setBoard(true), 900);
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    const io = new IntersectionObserver(
      (es) => {
        for (const e of es)
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
      },
      { root: el, threshold: 0.16 },
    );
    for (const n of el.querySelectorAll('.rv')) io.observe(n);
    if (location.hash) setTimeout(() => el.querySelector(location.hash)?.scrollIntoView(), 60);
    return () => {
      el.removeEventListener('scroll', onScroll);
      io.disconnect();
      s.dispose();
    };
  }, []);

  const toChandigarh = () => root.current?.querySelector('#chandigarh')?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });

  return (
    <ScenesCtx.Provider value={scenes}>
      <div className="gx kz" ref={root}>
        <div className="gx-progress" aria-hidden />
        <div className="kz-grain" aria-hidden />
        <SiteNav scroller={root} />
        <main>
          <Hero onDescend={toChandigarh} />
          <PaperIndia onChandigarh={toChandigarh} />
          <PaperCity />
          <Inside />
          <Stage />

          {/* ───── your name ───── */}
          <section className="gx-sec cl" id="name" aria-labelledby="cl-title">
            <div className="cl-copy rv">
              <p className="gx-kicker mono">05 · Your name</p>
              <h2 id="cl-title">
                One name.
                <br />
                <em>Every world.</em>
              </h2>
              <p className="dim">Your GG Bharat name is how people know you in every game, show and event we make. It’s one of a kind: once it’s yours, nobody else can ever have it.</p>
              <ol className="cl-steps">
                {[
                  ['Pick it', 'Type a name: your pass shows it, and we check at once that it’s free.'],
                  [`Pay ₹${REG.fee}`, 'UPI, card or net banking, through Razorpay. Taxes included. One name a person.'],
                  ['Keep it', 'Your founder pass, numbered in order. Make your free account with the same email, and on launch day you just log in.'],
                ].map(([t, d], i) => (
                  <li key={t}>
                    <b className="mono">{String(i + 1).padStart(2, '0')}</b>
                    <div>
                      <h3>{t}</h3>
                      <p>{d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <div className="rv" style={{ '--d': '0.1s' } as React.CSSProperties}>
              <Claim id="gp-name-mid" />
            </div>
          </section>

          {/* ───── prices ───── */}
          <section className="gx-sec pr" id="price" aria-labelledby="pr-title">
            <header className="gx-head rv">
              <p className="gx-kicker mono">06 · Prices</p>
              <h2 id="pr-title">
                A small fee now.
                <br />
                <em>One price later.</em>
              </h2>
            </header>
            <div className="pr-tickets">
              <div className="pr-ticket rv">
                <div className="t-head mono">
                  <b>Name pass</b>
                  <span>Now</span>
                </div>
                <p className="amt">
                  <span>₹</span>
                  {REG.fee}
                </p>
                <ul>
                  <li>Your name in every GG Bharat world, for good</li>
                  <li>A founder number, in order of registration</li>
                  <li>First in when Chandigarh opens</li>
                  <li>{REG.refundDays}-day full refund</li>
                </ul>
                <button type="button" className="gx-btn" onClick={() => openRegister()}>
                  Join the waitlist <span aria-hidden>→</span>
                </button>
                <p className="t-fine mono">One-time · taxes included · paid through Razorpay</p>
              </div>
              <div className="pr-ticket alt rv" style={{ '--d': '0.1s' } as React.CSSProperties}>
                <div className="t-head mono">
                  <b>{PASS.name}</b>
                  <span>At launch</span>
                </div>
                <p className="amt">
                  <span>₹</span>
                  {PASS.price}
                </p>
                <ul>
                  {PASS.includes.map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
                <p className="t-fine mono">One-time · taxes included · on sale when the game opens</p>
              </div>
            </div>
          </section>

          {/* ───── questions ───── */}
          <section className="gx-sec gx-faq" id="faq" aria-labelledby="faq-title">
            <header className="gx-head rv">
              <p className="gx-kicker mono">07 · Questions</p>
              <h2 id="faq-title">
                Good to <em>know.</em>
              </h2>
            </header>
            <div className="gx-qs">
              {FAQ.map(([q, a], i) => (
                <div key={q} className={`gx-q${faq === i ? ' open' : ''}`}>
                  <button type="button" aria-expanded={faq === i} onClick={() => setFaq(faq === i ? null : i)}>
                    <span className="mono n">{String(i + 1).padStart(2, '0')}</span>
                    <span className="t">{q}</span>
                    <i aria-hidden />
                  </button>
                  <div className="a">
                    <p>{a}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ───── the way in ───── */}
          <section className="kz-final" id="register" aria-labelledby="fin-title">
            <div className="bg" aria-hidden>
              <i />
              <i />
              <i />
            </div>
            <div className="rv inner">
              <p className="gx-kicker mono">Last call</p>
              <h2 id="fin-title">
                See you in
                <br />
                <em>Chandigarh.</em>
              </h2>
              <Claim id="gp-name-final" />
              <p className="kz-acct">
                Already on the waitlist? <a {...linkProps('/login')}>Log in</a> to see your place, or <a {...linkProps('/login?mode=signup')}>make your free account</a>.
              </p>
            </div>
          </section>
        </main>
        <SiteFooter />
        <SpeedCam scroller={root} />
        {board && <PlayCard onClose={() => setBoard(false)} />}
      </div>
    </ScenesCtx.Provider>
  );
}
